import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import {
    IPositionsForm,
    IRequestMaterialSinglePosition,
    IRequestMaterialStageContext,
    useCreateSupplyRequestQuery,
    useGetEstimateSupplyRequestPositionList,
} from '@entities/requestMaterial';
import {
    ICreateSupplyRequestExtraPosition,
    ICreateSupplyRequestPosition,
} from '@entities/requestMaterial/api/api-funcs';
import { showToast } from '@features';
import { DateHelpers } from '@shared/helpers';

export const useAddRequestMaterialModal = (onClose: () => void) => {
    const { t } = useTranslation();

    const { data: stages = [] } = useGetEstimateSupplyRequestPositionList();

    const [search, setSearch] = useState('');
    const onSearchChange = (_: string, value: string) => setSearch(value);

    const [positionsForm, setPositionsForm] = useState<Record<string, IPositionsForm>>({});

    const [manualPositions, setManualPositions] = useState<IRequestMaterialSinglePosition[]>([]);
    const [newlyAddedGuid, setNewlyAddedGuid] = useState<string | null>(null);

    const addManualPosition = () => {
        const newPosition: IRequestMaterialSinglePosition = {
            type: 'single',
            guid: uuidv4(),
            name: '',
            unit: '',
            remainAmount: '-',
            deliveryDate: null,
            canceled: false,
            isManual: true,
        };
        setNewlyAddedGuid(newPosition.guid);
        setManualPositions((prev) => [newPosition, ...prev]);
    };

    const [selectedGuids, setSelectedGuids] = useState<Set<string>>(new Set());
    const [invalidGuids, setInvalidGuids] = useState<Set<string>>(new Set());
    const [isDeliveryDateModalOpen, setIsDeliveryDateModalOpen] = useState(false);

    const onChangePositionForm = (guid: string, name: keyof IPositionsForm, value: string) => {
        setPositionsForm((prev) => ({
            ...prev,
            [guid]: {
                ...prev[guid],
                [name]: value,
            },
        }));
        setSelectedGuids((prev) => {
            if (prev.has(guid)) return prev;
            return new Set(prev).add(guid);
        });
        if (name === 'name' && value) {
            setInvalidGuids((prev) => {
                if (!prev.has(guid)) return prev;
                const next = new Set(prev);
                next.delete(guid);
                return next;
            });
        }
    };

    const toggleSelected = (guid: string) => {
        setSelectedGuids((prev) => {
            const next = new Set(prev);
            next.has(guid) ? next.delete(guid) : next.add(guid);
            return next;
        });
    };

    const manualGuids = new Set(manualPositions.map((p) => p.guid));

    const allPositions: IRequestMaterialSinglePosition[] = [
        ...manualPositions,
        ...stages.flatMap((stage) =>
            stage.positions.flatMap((p) => (p.type === 'single' ? [p] : p.children))
        ),
    ];

    const { mutate: createRequest } = useCreateSupplyRequestQuery();

    const buildPayloadAndSubmit = (fallbackDate?: string) => {
        const positions: ICreateSupplyRequestPosition[] = [];
        const extraPositions: ICreateSupplyRequestExtraPosition[] = [];
        // @ts-ignore
        for (const guid of selectedGuids) {
            const form = positionsForm[guid];
            const position = allPositions.find((p) => p.guid === guid)!;
            const date = form?.date || fallbackDate || '';

            if (manualGuids.has(guid)) {
                extraPositions.push({
                    name: form?.name ?? position.name,
                    unit: form?.unit ?? position.unit,
                    amount: parseFloat(form?.count ?? '0'),
                    required_delivery_date: DateHelpers.dateFormatter({
                        date,
                        format: 'YYYY-MM-DD',
                    }),
                    ...(form?.comment ? { comment: form.comment } : {}),
                });
            } else {
                positions.push({
                    guid,
                    amount: parseFloat(form?.count ?? '0'),
                    required_delivery_date: DateHelpers.dateFormatter({
                        date,
                        format: 'YYYY-MM-DD',
                    }),
                    ...(form?.comment ? { comment: form.comment } : {}),
                });
            }
        }

        createRequest({
            ...(positions.length > 0 ? { positions } : {}),
            ...(extraPositions.length > 0 ? { extra_positions: extraPositions } : {}),
        });
    };

    const validateAndSubmit = () => {
        const emptyGuids = new Set<string>();
        // @ts-ignore
        for (const guid of selectedGuids) {
            const position = allPositions.find((p) => p.guid === guid);
            const effectiveName = positionsForm[guid]?.name ?? position?.name ?? '';
            if (!effectiveName.trim()) {
                emptyGuids.add(guid);
            }
        }

        if (emptyGuids.size > 0) {
            setInvalidGuids(emptyGuids);
            return;
        }
        // @ts-ignore
        const hasMissingDate = [...selectedGuids].some((guid) => !positionsForm[guid]?.date);

        if (hasMissingDate) {
            setIsDeliveryDateModalOpen(true);
            return;
        }

        buildPayloadAndSubmit();
        showToast(t('useAddRequestMaterialModal.validateAndSubmit.showToast'));
        onClose();
    };

    const onDeliveryDateSave = (date: string) => {
        setIsDeliveryDateModalOpen(false);
        buildPayloadAndSubmit(date);
        showToast(t('useAddRequestMaterialModal.validateAndSubmit.showToast'));
        onClose();
    };

    const onDeliveryDateBack = () => {
        setIsDeliveryDateModalOpen(false);
    };

    const context: IRequestMaterialStageContext = {
        selectedGuids,
        toggleSelected,
        positionsForm,
        onChangePositionForm,
        newlyAddedGuid,
        invalidGuids,
    };

    return {
        t,
        context,
        selectedGuids,
        stages,
        search,
        onSearchChange,
        manualPositions,
        addManualPosition,
        validateAndSubmit,
        isDeliveryDateModalOpen,
        onDeliveryDateSave,
        onDeliveryDateBack,
    };
};
